import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils";

const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center rounded-[3px] text-sm font-medium tracking-tight whitespace-nowrap transition-colors outline-none focus-visible:ring-1 focus-visible:ring-wiki-accent focus-visible:ring-offset-2 focus-visible:ring-offset-wiki-bg disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-wiki-accent text-wiki-bg shadow-[inset_0_1px_0_rgba(255,235,200,0.35),0_1px_0_color-mix(in_srgb,var(--color-wiki-accent)_25%,transparent)] hover:bg-wiki-accent-soft active:translate-y-px",
        secondary:
          "border border-wiki-border bg-wiki-bg-3 text-wiki-fg-1 hover:border-wiki-border-2 hover:bg-wiki-bg-hover",
        outline:
          "border border-wiki-border bg-transparent text-wiki-fg-2 hover:border-wiki-border-2 hover:bg-wiki-bg-3/40 hover:text-wiki-fg-1",
        ghost:
          "bg-transparent text-wiki-fg-2 hover:bg-wiki-bg-3 hover:text-wiki-fg-1",
        destructive:
          "border border-red-900/40 bg-red-950/40 text-red-300 hover:bg-red-950/60 hover:text-red-200",
      },
      size: {
        default: "h-9 px-4 has-[>svg]:px-3",
        sm: "h-7 gap-1.5 px-2.5 text-xs has-[>svg]:px-2",
        lg: "h-11 px-6 text-[15px] has-[>svg]:px-4",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button };
